import React, { createContext, useContext, useEffect, useState } from "react";
import { OrganizationsOutput, UserRoles } from "@/interfaces";
import { PlansOutput } from "@/interfaces/plan";
import { PlansService } from "@/services/PlansService";

interface AppManagerContextType {
  currentOrganization: OrganizationsOutput | null;
  setCurrentOrganization: (org: OrganizationsOutput | null) => void;
  currentPlan: PlansOutput | null;
  currentRole: UserRoles;
  setCurrentRole: (role: UserRoles) => void;
}

const STORAGE_KEYS = {
  ORGANIZATION: "app_current_organization",
  PLAN: "app_current_plan",
  ROLE: "app_current_role",
};

const OrganizationContext = createContext<AppManagerContextType | undefined>(
  undefined
);

export const AppManagerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Initialize state from localStorage if available
  const [currentOrganization, setCurrentOrganization] =
    useState<OrganizationsOutput | null>(() => {
      const stored = localStorage.getItem(STORAGE_KEYS.ORGANIZATION);
      return stored ? JSON.parse(stored) : null;
    });

  const [currentPlan, setCurrentPlan] = useState<PlansOutput | null>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.PLAN);
    return stored ? JSON.parse(stored) : null;
  });

  const [currentRole, setCurrentRole] = useState<UserRoles>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.ROLE);
    return stored ? JSON.parse(stored) : UserRoles.MEMBER;
  });

  // Persist organization changes to localStorage
  useEffect(() => {
    if (currentOrganization) {
      localStorage.setItem(
        STORAGE_KEYS.ORGANIZATION,
        JSON.stringify(currentOrganization)
      );
    } else {
      localStorage.removeItem(STORAGE_KEYS.ORGANIZATION);
    }
  }, [currentOrganization]);

  // Persist role changes to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.ROLE, JSON.stringify(currentRole));
  }, [currentRole]);

  // Fetch and persist plan when organization changes
  useEffect(() => {
    const fetchCurrentPlan = async () => {
      if (currentOrganization?.plan?.planId) {
        try {
          const plansService = new PlansService();
          const plan = await plansService.findById(
            currentOrganization.plan.planId,
            { expand: "features" }
          );
          setCurrentPlan(plan);
          localStorage.setItem(STORAGE_KEYS.PLAN, JSON.stringify(plan));
        } catch (error) {
          console.error("Error fetching plan:", error);
        }
      } else {
        setCurrentPlan(null);
        localStorage.removeItem(STORAGE_KEYS.PLAN);
      }
    };

    fetchCurrentPlan();
  }, [currentOrganization]);

  // Wrapper functions to handle both state and storage updates
  const handleSetCurrentOrganization = (org: OrganizationsOutput | null) => {
    setCurrentOrganization(org);
  };

  const handleSetCurrentRole = (role: UserRoles) => {
    setCurrentRole(role);
  };

  return (
    <OrganizationContext.Provider
      value={{
        currentOrganization,
        setCurrentOrganization: handleSetCurrentOrganization,
        currentPlan,
        currentRole,
        setCurrentRole: handleSetCurrentRole,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

export const useAppManager = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error(
      "useOrganization must be used within an OrganizationProvider"
    );
  }
  return context;
};
